import * as Ably from 'ably';
import config from '../../config';
import { logglyLogger } from '../../services/logglyLogger';

interface GPSLocationCoord {
  latitude: number,
  longitude: number,
}

export interface SubscribeCallbackData {
  timestamp: number;
  coords: GPSLocationCoord;
  heading?: number;
}

const parseAblyMessage = (message: Ably.Types.Message) => {
  const { timestamp, data: { latitude, longitude, heading } } = message;
  return { timestamp, coords: { latitude, longitude }, heading };
};

class GPSLocationClient {
  private connection?: Ably.Realtime;

  private channels: { [k: string]: Ably.Types.RealtimeChannelCallbacks } = {};

  isConnected = false;

  private ablyChannelEnv = import.meta.env.REACT_APP_ABLY_CHANNEL_ENV;

  setupConnection(userId: string) {
    if (!this.ablyChannelEnv) {
      logglyLogger.log({ error: 'No Ably Channel Key Detected', info: 'When setting up GPSLocationClients, the ably env key was not found for the channel' });
      throw Error('No Ably Channel Key detected in env');
    }
    const ably = new Ably.Realtime({
      clientId: userId,
      key: config.thirdParty.ablySubscribeKey,
      queueMessages: false,
      autoConnect: false,
    });

    ably.connection.on('connected', () => {
      this.isConnected = true;
    });

    ably.connection.on('disconnected', () => {
      this.isConnected = false;
    });

    this.connection = ably;
  }

  connect() {
    this.connection?.connect();
  }

  close() {
    try {
      this.connection?.close();
    } catch (error) {
      console.warn('GPSLocationClient: Error closing connection', error);
    }
    this.isConnected = false;
  }

  subscribe(projectId: string | number, routeId: string, callback: (data: SubscribeCallbackData) => void) {
    if (!this.isChannelExists(projectId, routeId)) {
      this.registerChannel(projectId, routeId, callback);
    }
  }

  async unsubscribeAll() {
    const { channels } = this;
    const channelKeys = Object.keys(channels);

    await Promise.allSettled(
      channelKeys.map(async (key) => {
        try {
          channels[key].unsubscribe();
          await channels[key].detach();
        } catch (error) {
          console.warn(`GPSLocationClient: Error detaching channel ${key}`, error);
        }
      }),
    );

    this.channels = {};
  }

  private getChannelId(projectId: string | number, routeId: string | number) {

    let key = `project:${projectId}:route:${routeId}`;
    if (this.ablyChannelEnv !== 'prod') {
      key += `:env:${this.ablyChannelEnv}`;
    }
    return key;
  }

  private isChannelExists(projectId: string | number, routeId: string | number) {
    const key = this.getChannelId(projectId, routeId);
    return this.channels[key] != null;
  }

  private registerChannel(projectId: string | number, routeId: (string | number), callback: (data: SubscribeCallbackData) => void) {
  
    if (this.connection == null) {
      logglyLogger.log({ error: 'No connection error', info: 'Attempting to register channel, but no connection found' });
      return;
    }

    const key = this.getChannelId(projectId, routeId);

    this.channels[key] = this.connection.channels.get(`[?rewind=1]${key}`);

    this.channels[key].subscribe((message) => {
      const parsedMessage = parseAblyMessage(message);

      if (parsedMessage.coords != null) {
        callback(parsedMessage);
      }
    });
  }
}

export default GPSLocationClient;
